import { DataExportLimit, DataExportType } from "@/api-schema-enums";
import { DataExportColumn, useDataExportColumnsQuery } from "@/common/api/data-export/use-data-export-columns.query";
import { useDataExportCreateMutation } from "@/common/api/data-export/use-data-export-create.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/ui/loader";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export interface DataExportDialogProps {
  type: DataExportType;
  query: object;
  body: unknown;
  defaultColumns: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ColumnGroup {
  name: string;
  columns: DataExportColumn[];
}

export function groupDataExportColumns(columns: DataExportColumn[]): ColumnGroup[] {
  const groups = new Map<string, DataExportColumn[]>();
  for (const column of columns) {
    groups.set(column.group, [...(groups.get(column.group) ?? []), column]);
  }
  return [...groups].map(([name, groupColumns]) => ({ name, columns: groupColumns }));
}

export function resolveDefaultDataExportColumns(catalog: DataExportColumn[], defaultColumns: string[]): string[] {
  const available = new Set(catalog.map((column) => column.path));
  return defaultColumns.filter((path) => available.has(path));
}

export function parseMaxRows(value: string): number | undefined {
  if (value.trim() === "") {
    return DataExportLimit.MaxRows;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 && parsed <= DataExportLimit.MaxRows ? parsed : undefined;
}

export function DataExportDialog(props: DataExportDialogProps) {
  const { type, open, onOpenChange } = props;
  const { t } = useTranslation(["common"]);
  const columnsQuery = useDataExportColumnsQuery(type, { enabled: open });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <DialogHeader className="pr-10">
          <DialogTitle>{t("common:ExportTitle")}</DialogTitle>
          <DialogDescription>{t("common:ExportDescription")}</DialogDescription>
        </DialogHeader>
        {columnsQuery.isPending && <Loader isChild />}
        {columnsQuery.isError && <p className="text-destructive text-sm">{t("common:ExportColumnsLoadError")}</p>}
        {columnsQuery.data && <DataExportForm {...props} catalog={columnsQuery.data} />}
      </DialogContent>
    </Dialog>
  );
}

function DataExportForm({
  type,
  query,
  body,
  defaultColumns,
  onOpenChange,
  catalog,
}: DataExportDialogProps & { catalog: DataExportColumn[] }) {
  const { t } = useTranslation(["common"]);
  const { mutateAsync, isPending } = useDataExportCreateMutation();
  const groups = useMemo(() => groupDataExportColumns(catalog), [catalog]);
  const [selected, setSelected] = useState(() => new Set(resolveDefaultDataExportColumns(catalog, defaultColumns)));
  const [maxRows, setMaxRows] = useState("");
  const parsedMaxRows = parseMaxRows(maxRows);
  const maxRowsInvalid = parsedMaxRows === undefined;

  const toggle = (path: string, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(path);
      } else {
        next.delete(path);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (parsedMaxRows === undefined) {
      return;
    }
    return toastMutationStatus({
      mutationFn: () =>
        mutateAsync({
          type,
          query: query as Record<string, unknown>,
          body: (body ?? {}) as Record<string, unknown>,
          columns: catalog.filter((column) => selected.has(column.path)).map((column) => column.path),
          maxRows: parsedMaxRows,
        }),
      errorMessage: t("common:ExportStartError"),
      customErrors: { 403: t("common:ExportNotAllowed") },
      onSuccess: () => {
        toast.success(t("common:ExportStarted"));
        onOpenChange(false);
      },
    });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-sm">
          {t("common:ExportSelectedColumns", { count: selected.size })}
        </span>
        <div className="flex gap-2">
          <Button
            variant={ButtonVariant.Ghost}
            size={ButtonSize.Sm}
            onClick={() => setSelected(new Set(catalog.map((column) => column.path)))}
          >
            {t("common:SelectAll")}
          </Button>
          <Button variant={ButtonVariant.Ghost} size={ButtonSize.Sm} onClick={() => setSelected(new Set())}>
            {t("common:ClearSelection")}
          </Button>
        </div>
      </div>
      <div className="flex max-h-[45vh] flex-col gap-4 overflow-y-auto pr-1">
        {groups.map((group) => (
          <fieldset key={group.name} className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold">{group.name || t("common:ExportMainTable")}</legend>
            {group.columns.map((column) => {
              const id = `data-export-column-${column.path}`;
              return (
                <div key={column.path} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={selected.has(column.path)}
                    onCheckedChange={(checked) => toggle(column.path, checked === true)}
                  />
                  <Label htmlFor={id} className="mb-0 cursor-pointer font-mono text-sm leading-4 font-normal">
                    {column.path}
                  </Label>
                </div>
              );
            })}
          </fieldset>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="data-export-max-rows">{t("common:ExportMaxRows")}</Label>
        <Input
          id="data-export-max-rows"
          type="number"
          min={1}
          max={DataExportLimit.MaxRows}
          step={1}
          value={maxRows}
          error={maxRowsInvalid}
          aria-invalid={maxRowsInvalid}
          aria-describedby={maxRowsInvalid ? "data-export-max-rows-error" : undefined}
          placeholder={t("common:ExportMaxRowsPlaceholder", { max: DataExportLimit.MaxRows })}
          onChange={(event) => setMaxRows(event.target.value)}
        />
        {maxRowsInvalid && (
          <p id="data-export-max-rows-error" className="text-error text-[0.8rem] font-medium">
            {t("common:ExportMaxRowsInvalid", { max: DataExportLimit.MaxRows })}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          onClick={() => onOpenChange(false)}
        >
          {t("common:Cancel")}
        </Button>
        <Button onClick={handleSubmit} disabled={isPending || selected.size === 0 || maxRowsInvalid}>
          {t("common:Export")}
        </Button>
      </DialogFooter>
    </>
  );
}
